import { CanActivateFn, Params } from '@angular/router';
import { inject } from '@angular/core';
import {
  isForbidden,
  Route,
  RouteParam,
  UrlTreeProviderService,
} from '@web/common';
import { isNil, isServerContext } from '@web/utils';
import { catchError, map, of, switchMap, throwError } from 'rxjs';
import { PublicBoxAuthService } from '@web/features/shop';

const getBoxesUrlTree = (provider: UrlTreeProviderService) => {
  return provider.urlTree((provider) => provider.routerLink(Route.PL_Boxes));
};

const getDetailsAuthorizationUrlTree = (
  provider: UrlTreeProviderService,
  slug: string,
  queryParams: Params,
) => {
  return provider.urlTree(
    (provider) =>
      provider.routerLink(Route.PL_BoxDetailsAuthorization, {
        [RouteParam.Slug]: slug,
      }),
    {
      queryParams,
      queryParamsHandling: 'merge',
    },
  );
};

export const PublicBoxAuthorizedDetailsGuard: CanActivateFn = (route) => {
  const publicBoxAuth = inject(PublicBoxAuthService);
  const isServer = isServerContext();
  const slug = route.paramMap.get(RouteParam.Slug);
  const provider = inject(UrlTreeProviderService);
  const queryParams = route.queryParams;

  // Po stronie serwera robię tymczasowe przekierowanie na stronę ładowania.
  if (isServer) {
    return false;
  }

  if (isNil(slug)) {
    return getBoxesUrlTree(provider);
  }

  return publicBoxAuth.isAuthorizationRequired(slug).pipe(
    switchMap((result) => {
      if (result) {
        return publicBoxAuth.fetchAuthorizedDetails(slug).pipe(
          map((details) => !!details),
          catchError((response) => {
            if (isForbidden(response)) {
              return of(
                getDetailsAuthorizationUrlTree(provider, slug, queryParams),
              );
            }

            return throwError(() => response);
          }),
        );
      }

      // Jeśli autoryzacja nie jest wymagana, to wpuszczam na widok.
      return of(true);
    }),
  );
};
